import { NOTIFICATION_TYPES } from "../../../../../config/constants";
import {
  Select,
  Button,
  Empty,
  Spin,
  Table,
  Space,
  Form,
  Input,
  Drawer,
  Popconfirm,
  message,
  Menu,
  Divider,
  Upload,
  UploadFile,
  AutoComplete,
} from "antd";
import "../TableSetup.css";
import {
  CopyOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  QuestionCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";

import styles from "../../table-setup/TableSetup.module.css";
import { ColumnsType } from "antd/lib/table";
import ReferenceService from "../../../../../services/references/ReferencesService";
import { UpdateReferenceModel } from "../../../../../models/references/UpdateReferenceModel";
import { CreateReferenceModel } from "../../../../../models/references/CreateReferenceModel";
import { UploadChangeParam } from "antd/lib/upload";
import saveAs from "file-saver";
import { useTranslation } from "react-i18next";
import NotificationsUtil from "../../../../shared/utils/NotificationsUtil";
import Datatable from "../../../../shared/components/Datatable";

enum RowAction {
  View = 1,
  Edit,
  Create,
  Upload,
  Download,
}

const TableSetup = ({
  newReferenceDrawerEnabled,
  setNewReferenceDrawerEnabled,
  downloadReferenceDrawerEnabled,
  setDownloadReferenceDrawerEnabled,
  downloadReferenceTemplateDrawerEnabled,
  uploadReferenceDrawerEnabled,
  setUploadReferenceDrawerEnabled,
}: {
  newReferenceDrawerEnabled: boolean;
  setNewReferenceDrawerEnabled: (val: boolean) => void;
  downloadReferenceDrawerEnabled: boolean;
  setDownloadReferenceDrawerEnabled: (val: boolean) => void;
  downloadReferenceTemplateDrawerEnabled: boolean;
  setDownloadReferenceTemplateDrawerEnabled: (val: boolean) => void;
  uploadReferenceDrawerEnabled: boolean;
  setUploadReferenceDrawerEnabled: (val: boolean) => void;
}) => {
  const { t, i18n } = useTranslation("translations");
  const [drawerForm] = Form.useForm();
  const { Option } = Select;
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [validationTypeInput, setValidationTypeInput] = useState<any>("");
  const [allowedValueInput, setAllowedValueInput] = useState("");
  const [references, setReferences] = useState<any>([]);
  const [validationTypes, setValidationTypes] = useState<any>([]);

  const [drawerTitle, setDrawerTitle] = useState<string>("");
  const [drawerAction, setDrawerAction] = useState<RowAction>();
  const [editedRow, setEditedRow] = useState<any>();
  const [updateRowAllowed, setUpdateRowAllowed] = useState(false);

  const [selectedValidationType, setSelectedValidationType] = useState<any>();
  const [selectedReferencesFile, setSelectedReferencesFile] =
    useState<any>(null);

  const showDrawer = (action: RowAction) => {
    setDrawerAction(action);
    switch (action) {
      case RowAction.View:
        setDrawerTitle(
          t("Page.Configuration.TableSetup.Tabs.References.Labels.RowDetails", {
            lngs: i18n.languages,
            defaultValue: "Row details",
          })!
        );
        setValidationTypeInput(selectedValidationType);
        drawerForm.setFieldsValue({ validationType: selectedValidationType });
        break;
      case RowAction.Create:
        setDrawerTitle(
          t(
            "Page.Configuration.TableSetup.Tabs.References.Labels.NewReference",
            { lngs: i18n.languages, defaultValue: "New reference" }
          )!
        );
        setValidationTypeInput(selectedValidationType);
        drawerForm.setFieldsValue({ validationType: selectedValidationType });
        break;
      case RowAction.Edit:
        setDrawerTitle(
          t(
            "Page.Configuration.TableSetup.Tabs.References.Labels.EditReference",
            { lngs: i18n.languages, defaultValue: "Edit reference" }
          )!
        );
        setValidationTypeInput(selectedValidationType);
        drawerForm.setFieldsValue({ validationType: selectedValidationType });
        break;
      case RowAction.Upload:
        setDrawerTitle(
          t(
            "Page.Configuration.TableSetup.Tabs.References.Labels.UploadReferences",
            { lngs: i18n.languages, defaultValue: "Upload references" }
          )!
        );
        setValidationTypeInput(selectedValidationType);
        drawerForm.setFieldsValue({ validationType: selectedValidationType });
        break;
      case RowAction.Download:
        setDrawerTitle(
          t(
            "Page.Configuration.TableSetup.Tabs.References.Labels.ActionDownloadReferences",
            { lngs: i18n.languages, defaultValue: "Download reference" }
          )!
        );
        setValidationTypeInput(selectedValidationType);
        drawerForm.setFieldsValue({ validationType: selectedValidationType });
        break;
      default:
        break;
    }
    setOpenDrawer(true);
  };

  const onClose = () => {
    setOpenDrawer(false);
    setAllowedValueInput("");
    setValidationTypeInput("");
    setEditedRow(null);
    setNewReferenceDrawerEnabled(false);
    setDownloadReferenceDrawerEnabled(false);
    setUploadReferenceDrawerEnabled(false);
    setSelectedReferencesFile(null);
  };

  const handleReferencesFileChange = (value: UploadChangeParam<UploadFile>) => {
    if (value.fileList.length === 0) {
      setSelectedReferencesFile(null);
    } else {
      setSelectedReferencesFile(value?.fileList[0].originFileObj);
    }
  };

  const preventRequest = () => false;
  const customRequest = (options: any) => {
    // options?.onSuccess?("ok")
  };

  const onValidationTypeInputChange = (value: string) => {
    setValidationTypeInput(value);
  };

  const onAllowedValueInputChange: React.ChangeEventHandler<
    HTMLInputElement
  > = (event) => {
    setAllowedValueInput(event.target.value);
  };

  const onDoubleClickRow = (record: any) => {
    showDrawer(RowAction.View);
    setAllowedValueInput(record.allowedValue);
    setValidationTypeInput(record.validationType);
    setEditedRow(record);

    drawerForm.setFieldsValue({ validationTypeInput: record.validationType });
    drawerForm.setFieldsValue({ allowedValueInput: record.allowedValue });
  };

  const onSaveNewReference = () => {
    if (editedRow?.id) {
      const model: UpdateReferenceModel = {
        id: editedRow.id,
        AllowedValue: allowedValueInput,
        ValidationType: validationTypeInput,
      };
      ReferenceService.updateReference(model)
        .then((response: any) => {
          setIsTableLoading(true);
          onClose();
          setAllowedValueInput("");
          setValidationTypeInput("");
          NotificationsUtil.openNotificationDialog({
            description: t(
              "Page.Configuration.TableSetup.Tabs.Reference.Notifications.Reference.UpdateSuccessDescription",
              {
                lngs: i18n.languages,
                defaultValue: "Reference edited successfully.",
              }
            )!,
            type: NOTIFICATION_TYPES.SUCCESS,
            logError: false,
          });
        })
        .catch((error: any) => {
          NotificationsUtil.openNotificationDialog({
            description: error.response.data,
            type: NOTIFICATION_TYPES.ERROR,
            logError: true,
          });
        });
    } else {
      const model: CreateReferenceModel = {
        AllowedValue: allowedValueInput,
        ValidationType: validationTypeInput,
      };
      ReferenceService.createReference(model)
        .then((response: any) => {
          setIsTableLoading(true);
          onClose();
          setAllowedValueInput("");
          setValidationTypeInput("");
          NotificationsUtil.openNotificationDialog({
            description: t(
              "Page.Configuration.TableSetup.Tabs.References.Notifications.Reference.CreateSuccessDescription",
              {
                lngs: i18n.languages,
                defaultValue: "Reference added successfully.",
              }
            )!,
            type: NOTIFICATION_TYPES.SUCCESS,
            logError: false,
          });
        })
        .catch((error: any) => {
          NotificationsUtil.openNotificationDialog({
            description: error.response.data,
            type: NOTIFICATION_TYPES.ERROR,
            logError: true,
          });
        });
    }
  };

  const onDeleteReference = (id: number) => {
    ReferenceService.deleteRefereence(id)
      .then((response: any) => {
        setIsTableLoading(true);
        NotificationsUtil.openNotificationDialog({
          description: t(
            "Page.Configuration.TableSetup.Tabs.Reference.Notifications.Reference.DeleteSuccessDescription",
            {
              lngs: i18n.languages,
              defaultValue: "Reference deleted successfully.",
            }
          )!,
          type: NOTIFICATION_TYPES.SUCCESS,
          logError: false,
        });
      })
      .catch((error: any) => {
        NotificationsUtil.openNotificationDialog({
          description: error.response.data,
          type: NOTIFICATION_TYPES.ERROR,
          logError: true,
        });
      });
  };

  const onCopyReference = (record: any) => {
    showDrawer(RowAction.Create);
    setAllowedValueInput(record.allowedValue);
    setValidationTypeInput(record.validationType);
  };

  const onEditReference = (record: any) => {
    showDrawer(RowAction.Edit);
    setAllowedValueInput(record.allowedValue);
    setValidationTypeInput(record.validationType);
    setEditedRow(record);
  };

  const cancel = (e: React.MouseEvent<HTMLElement>) => {
    message.error("Click on No");
  };

  function onlyUnique(value: any, index: any, self: any) {
    return self.indexOf(value) === index;
  }

  useEffect(() => {
    if (newReferenceDrawerEnabled) {
      showDrawer(RowAction.Create);
    }
  }, [newReferenceDrawerEnabled]);

  useEffect(() => {
    if (downloadReferenceDrawerEnabled) {
      showDrawer(RowAction.Download);
    }
  }, [downloadReferenceDrawerEnabled]);

  useEffect(() => {
    if (downloadReferenceTemplateDrawerEnabled) {
      const model: any = {
        validationType: "downloadTemplate",
      };
      ReferenceService.downloadReferences(model).then((response: any) => {
        let url = window.URL.createObjectURL(new Blob([response.data]));
        saveAs(url, "references.xlsx");
      });
    }
  }, [downloadReferenceTemplateDrawerEnabled]);

  useEffect(() => {
    if (uploadReferenceDrawerEnabled) {
      showDrawer(RowAction.Upload);
    }
  }, [uploadReferenceDrawerEnabled]);

  useEffect(() => {
    if (isTableLoading) {
      ReferenceService.getAllReferences().then((response: any) => {
        const data = response.data;
        setReferences(
          data.filter((x: any) => x.validationType === selectedValidationType)
        );
        setValidationTypes(
          data.map((x: any) => x.validationType).filter(onlyUnique)
        );
        setIsTableLoading(false);
      });
    }
  }, [isTableLoading, selectedValidationType]);

  useEffect(() => {
    if (selectedValidationType) {
      setValidationTypeInput(selectedValidationType);
    }
  }, [selectedValidationType]);

  useEffect(() => {
    if (allowedValueInput && validationTypeInput && editedRow) {
      const def = references.find((x: any) => x.id === editedRow.id);

      if (
        allowedValueInput !== def.allowedValue ||
        validationTypeInput !== def.validationType
      ) {
        setUpdateRowAllowed(true);
      } else {
        setUpdateRowAllowed(false);
      }
    } else {
      setUpdateRowAllowed(false);
    }
  }, [allowedValueInput, validationTypeInput, editedRow, references]);

  const handleValidationTypeChange = (value: string) => {
    setSelectedValidationType(value);
    setIsTableLoading(true);
  };
  const handleTableMenuClick = (e: any) => {
    setSelectedValidationType(e.key);
    setIsTableLoading(true);
  };

  const downloadReferences = () => {
    const model: any = {
      validationType: validationTypeInput,
    };
    ReferenceService.downloadReferences(model).then((response: any) => {
      let url = window.URL.createObjectURL(new Blob([response.data]));
      saveAs(url, "references.xlsx");
    });
  };

  const uploadReferences = () => {
    const model: any = {
      file: selectedReferencesFile,
      validationType: validationTypeInput,
    };

    ReferenceService.uploadReferences(model)
      .then((resposne: any) => {
        setIsTableLoading(true);
        onClose();
        setValidationTypeInput("");
        NotificationsUtil.openNotificationDialog({
          description: t(
            "Page.Configuration.TableSetup.Tabs.References.Notifications.Reference.UploadSuccessDescription",
            {
              lngs: i18n.languages,
              defaultValue: "References upload succeeded.",
            }
          )!,
          type: NOTIFICATION_TYPES.SUCCESS,
          logError: false,
        });
      })
      .catch((error: any) => {
        NotificationsUtil.openNotificationDialog({
          description: error.response.data,
          type: NOTIFICATION_TYPES.ERROR,
          logError: true,
        });
      });
  };

  const deleteValidationType = () => {
    ReferenceService.deleteRefereences(selectedValidationType)
      .then((response: any) => {
        setIsTableLoading(true);
        setSelectedValidationType(undefined);
        NotificationsUtil.openNotificationDialog({
          description: t(
            "Page.Configuration.TableSetup.Tabs.References.Notifications.References.DeleteKeySuccess",
            {
              lngs: i18n.languages,
              defaultValue: "Validation type deleted successfully.",
            }
          )!,
          type: NOTIFICATION_TYPES.SUCCESS,
          logError: false,
        });
      })
      .catch((error: any) => {
        NotificationsUtil.openNotificationDialog({
          description: error.response.data,
          type: NOTIFICATION_TYPES.ERROR,
          logError: true,
        });
      });
  };

  interface DataType {
    id: number;
    key: number;
    validationType: string;
    allowedValue: string;
  }

  const columnsTable: ColumnsType<DataType> = [
    {
      title: t(
        "Page.Configuration.TableSetup.Tabs.References.Table.Headers.ValidationType",
        {
          lngs: i18n.languages,
          defaultValue: "Validation type",
        }
      ),
      dataIndex: "validationType",
      key: "validationType",
      sorter: (a, b) => a.validationType.localeCompare(b.validationType),
    },
    {
      title: t(
        "Page.Configuration.TableSetup.Tabs.References.Table.Headers.Allowed Value",
        {
          lngs: i18n.languages,
          defaultValue: "Allowed value",
        }
      ),
      dataIndex: "allowedValue",
      key: "allowedValue",
      sorter: (a, b) => a.allowedValue.localeCompare(b.allowedValue),
    },
  ];

  const scroll = { y: "calc(70vh - 10em)" };

  return (
    <div className="table-setup-tab-container">
      <div className="section-container-sm">
        <div className="section-header">
          <Select
            className={styles.selectWidth}
            showSearch={true}
            showArrow={true}
            allowClear={true}
            value={selectedValidationType}
            onChange={handleValidationTypeChange}
            style={{ flex: 2 }}
            placeholder={t(
              "Page.Configuration.TableSetup.Tabs.References.Placeholders.SelectValidationType",
              {
                lngs: i18n.languages,
                defaultValue: "Select Validation type",
              }
            )}
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option!.children as unknown as string).includes(input)
            }
            filterSort={(optionA, optionB) =>
              (optionA!.children as unknown as string)
                .toLowerCase()
                .localeCompare(
                  (optionB!.children as unknown as string).toLowerCase()
                )
            }
          >
            {validationTypes.map((item: any) => {
              return (
                <Option value={item ? item : "no-type"}>
                  {item ? item : "No type"}
                </Option>
              );
            })}
          </Select>
        </div>
        <Divider />
        <div className="section-items">
          {!isTableLoading && (
            <Menu
              items={validationTypes.map((item: any) => {
                return {
                  key: item ? item : "no-key",
                  label: item ? item : "No key",
                };
              })}
              selectedKeys={[selectedValidationType]}
              onClick={handleTableMenuClick}
            />
          )}
          {isTableLoading && (
            <div className={styles.loadingContainer}>
              <Spin />
            </div>
          )}
        </div>
      </div>
      <div className="data-container">
        <>
          {selectedValidationType && (
            <>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Popconfirm
                  style={{ width: "400px" }}
                  placement="topRight"
                  title="Are you sure you want to delete selected validation type?"
                  icon={<QuestionCircleOutlined style={{ color: "red" }} />}
                  onConfirm={() => deleteValidationType()}
                  // onCancel={() => cancel}
                  okText="Delete"
                  cancelText="Cancel"
                >
                  <Button size="small" danger icon={<DeleteOutlined />}>
                    Delete Validation Type
                  </Button>
                </Popconfirm>
              </div>
              <Divider />
            </>
          )}
          {/* <div className="title-actions">
            <div className="title-info">
              <span>References</span>
              <p>
                Contextual information frame with Lorem ipsum dolor sit amet,
                consectetur adipiscing elit. Proin sit amet auctor lacus, vel
                ornare lacus. Donec at aliquam purus. Vivamus non tincidunt
                ipsum, non ultrices orci.{" "}
              </p>
            </div>
            <div className="header-buttons">
              <Button
                icon={<PlusOutlined />}
                size="small"
                type="ghost"
                onClick={() => showDrawer(RowAction.Create)}
              >
                Add reference
              </Button>
              <Button
                disabled
                icon={<ClearOutlined />}
                size="small"
                // className="update-btn"
              >
                Clear all filters
              </Button>
            </div>
          </div> */}
          <div className="table-container">
            {!selectedValidationType ? (
              <Empty
                description={t(
                  "Page.Configuration.TableSetup.Tabs.References.Tutorial.GetStarted",
                  {
                    lngs: i18n.languages,
                    defaultValue:
                      "Select a Validation type on the left to get started",
                  }
                )}
              />
            ) : (
              <Datatable
                scroll={scroll}
                tableColumns={columnsTable}
                data={references}
                setData={setReferences}
                isTableLoading={isTableLoading}
                otherActions={[]}
                otherRowEnabled={false}
                editRowEnabled={true}
                copyRowEnabled={true}
                deleteRowEnabled={true}
                onEditFunc={onEditReference}
                onCopyFunc={onCopyReference}
                onDeleteFunc={onDeleteReference}
                onDoubleClickRow={onDoubleClickRow}
                selectable={false}
                multiSelect={false}
                selectedRows={[]}
                setSelectedRows={undefined}
                checkPermissions={true}
              ></Datatable>
              // <Table
              //   // locale={locale}
              //   // components={tableItemsComponents}
              //   dataSource={references}
              //   columns={columnsTable}
              //   loading={isTableLoading}
              //   size="small"
              //   onRow={(record, rowIndex) => {
              //     return {
              //       onDoubleClick: (event) => {
              //         showDrawer(RowAction.View);
              //         onDoubleClickRow(record);
              //       },
              //     };
              //   }}
              // />
            )}
          </div>
        </>
        <Drawer
          width={"400px"}
          getContainer={false}
          style={{ position: "absolute" }}
          title={drawerTitle}
          placement="bottom"
          onClose={onClose}
          visible={openDrawer}
          closable={false}
          extra={
            <Space>
              {RowAction?.View === drawerAction && (
                <>
                  <Button
                    onClick={() => showDrawer(RowAction.Edit)}
                    icon={<EditOutlined />}
                    size="small"
                  >
                    Edit
                  </Button>
                  <Button
                    type="ghost"
                    onClick={onClose}
                    icon={<CloseOutlined />}
                    size="small"
                  />
                </>
              )}
              {RowAction?.Create === drawerAction && (
                <>
                  <Button
                    disabled={!allowedValueInput || !validationTypeInput}
                    className={
                      !allowedValueInput || !validationTypeInput
                        ? ""
                        : "active-btn"
                    }
                    onClick={onSaveNewReference}
                    icon={<CheckOutlined />}
                    size="small"
                  >
                    {t("General.Labels.Save", {
                      lngs: i18n.languages,
                      defaultValue: "Save",
                    })}
                  </Button>
                  <Button
                    type="ghost"
                    onClick={onClose}
                    icon={<CloseOutlined />}
                    size="small"
                  />
                </>
              )}
              {RowAction?.Edit === drawerAction && (
                <>
                  <Button
                    disabled={!updateRowAllowed}
                    className={!updateRowAllowed ? "" : "active-btn"}
                    onClick={onSaveNewReference}
                    icon={<CheckOutlined />}
                    size="small"
                  >
                    {t("General.Labels.Update", {
                      lngs: i18n.languages,
                      defaultValue: "Update",
                    })}
                  </Button>
                  <Button
                    type="ghost"
                    onClick={onClose}
                    icon={<CloseOutlined />}
                    size="small"
                  />
                </>
              )}
              {RowAction?.Download === drawerAction && (
                <>
                  <Button
                    disabled={!validationTypeInput}
                    className={!validationTypeInput ? "" : "active-btn"}
                    onClick={downloadReferences}
                    icon={<CheckOutlined />}
                    size="small"
                  >
                    {t("General.Labels.Download", {
                      lngs: i18n.languages,
                      defaultValue: "Download",
                    })}
                  </Button>
                  <Button
                    type="ghost"
                    onClick={onClose}
                    icon={<CloseOutlined />}
                    size="small"
                  />
                </>
              )}
              {RowAction?.Upload === drawerAction && (
                <>
                  <Button
                    disabled={!selectedReferencesFile || !validationTypeInput}
                    className={
                      !selectedReferencesFile || !validationTypeInput
                        ? ""
                        : "active-btn"
                    }
                    onClick={uploadReferences}
                    icon={<CheckOutlined />}
                    size="small"
                  >
                    {t("General.Labels.StartUpload", {
                      lngs: i18n.languages,
                      defaultValue: "Start upload",
                    })}
                  </Button>
                  <Button
                    type="ghost"
                    onClick={onClose}
                    icon={<CloseOutlined />}
                    size="small"
                  />
                </>
              )}
            </Space>
          }
        >
          {RowAction.View === drawerAction && (
            <Form
              form={drawerForm}
              layout="inline"
              // wrapperCol={{ span: 12 }}
              labelCol={{ span: 12 }}
              disabled={RowAction.View === drawerAction ? true : false}
            >
              <Form.Item
                name="validationType"
                label={t(
                  "Page.Configuration.TableSetup.Tabs.References.Labels.ValidationType",
                  {
                    lngs: i18n.languages,
                    defaultValue: "Validation type:",
                  }
                )}
                tooltip={t(
                  "Page.Configuration.TableSetup.Tabs.References.Tooltips.ValidationType",
                  {
                    lngs: i18n.languages,
                    defaultValue: "",
                  }
                )}
                style={{ width: "300px" }}
              >
                <AutoComplete
                  showSearch={true}
                  showArrow={true}
                  allowClear={true}
                  value={validationTypeInput}
                  onChange={onValidationTypeInputChange}
                  style={{ flex: 2 }}
                  placeholder={t("General.Placeholders.EnterValue", {
                    lngs: i18n.languages,
                    defaultValue: "Enter value",
                  })}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option!.children as unknown as string).includes(input)
                  }
                  filterSort={(optionA, optionB) =>
                    (optionA!.children as unknown as string)
                      .toLowerCase()
                      .localeCompare(
                        (optionB!.children as unknown as string).toLowerCase()
                      )
                  }
                >
                  {validationTypes.map((item: any) => {
                    return (
                      <Option value={item ? item : "no-type"}>
                        {item ? item : "No type"}
                      </Option>
                    );
                  })}
                </AutoComplete>
              </Form.Item>
              <Form.Item
                label={t(
                  "Page.Configuration.TableSetup.Tabs.References.Labels.AllowedValue",
                  {
                    lngs: i18n.languages,
                    defaultValue: "Allowed value:",
                  }
                )}
                tooltip={t(
                  "Page.Configuration.TableSetup.Tabs.References.Tooltips.AllowedValue",
                  {
                    lngs: i18n.languages,
                    defaultValue: "",
                  }
                )}
              >
                <Input
                  value={allowedValueInput}
                  placeholder={
                    t("General.Placeholders.EnterValue", {
                      lngs: i18n.languages,
                      defaultValue: "Enter value",
                    })!
                  }
                  onChange={onAllowedValueInputChange}
                />
              </Form.Item>
            </Form>
          )}
          {(RowAction.Create === drawerAction ||
            RowAction.Edit === drawerAction) && (
            <Form
              form={drawerForm}
              layout="inline"
              // wrapperCol={{ span: 12 }}
              labelCol={{ span: 12 }}
              // disabled={RowAction.View === drawerAction ? true : false}
            >
              <Form.Item
                name="validationType"
                label={t(
                  "Page.Configuration.TableSetup.Tabs.References.Labels.ValidationType",
                  {
                    lngs: i18n.languages,
                    defaultValue: "Validation type:",
                  }
                )}
                tooltip={t(
                  "Page.Configuration.TableSetup.Tabs.References.Tooltips.ValidationType",
                  {
                    lngs: i18n.languages,
                    defaultValue: "",
                  }
                )}
                style={{ width: "300px" }}
              >
                <AutoComplete
                  showSearch={true}
                  showArrow={true}
                  allowClear={true}
                  value={validationTypeInput}
                  onChange={onValidationTypeInputChange}
                  style={{ flex: 2 }}
                  placeholder={t("General.Placeholders.EnterValue", {
                    lngs: i18n.languages,
                    defaultValue: "Enter value",
                  })}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option!.children as unknown as string).includes(input)
                  }
                  filterSort={(optionA, optionB) =>
                    (optionA!.children as unknown as string)
                      .toLowerCase()
                      .localeCompare(
                        (optionB!.children as unknown as string).toLowerCase()
                      )
                  }
                >
                  {validationTypes.map((item: any) => {
                    return (
                      <Option value={item ? item : "no-type"}>
                        {item ? item : "No type"}
                      </Option>
                    );
                  })}
                </AutoComplete>
              </Form.Item>
              <Form.Item
                label={t(
                  "Page.Configuration.TableSetup.Tabs.References.Labels.AllowedValue",
                  {
                    lngs: i18n.languages,
                    defaultValue: "Allowed value:",
                  }
                )}
                tooltip={t(
                  "Page.Configuration.TableSetup.Tabs.References.Tooltips.AllowedValue",
                  {
                    lngs: i18n.languages,
                    defaultValue: "",
                  }
                )}
              >
                <Input
                  value={allowedValueInput}
                  placeholder={
                    t("General.Placeholders.EnterValue", {
                      lngs: i18n.languages,
                      defaultValue: "Enter value",
                    })!
                  }
                  onChange={onAllowedValueInputChange}
                />
              </Form.Item>
            </Form>
          )}
          {(RowAction.Upload === drawerAction ||
            RowAction.Download === drawerAction) && (
            <Form
              form={drawerForm}
              layout="vertical"
              // wrapperCol={{ span: 12 }}
              // labelCol={{ span: 12 }}
              // disabled={RowAction.View === drawerAction ? true : false}
            >
              <div className="translation_top_form">
                <Form.Item
                  name="validationType"
                  label={t(
                    "Page.Configuration.TableSetup.Tabs.References.Labels.ValidationType",
                    {
                      lngs: i18n.languages,
                      defaultValue: "Validation type:",
                    }
                  )}
                  tooltip={t(
                    "Page.Configuration.TableSetup.Tabs.References.Tooltips.ValidationType",
                    {
                      lngs: i18n.languages,
                      defaultValue: "",
                    }
                  )}
                  style={{ width: "300px" }}
                >
                  <AutoComplete
                    showSearch={true}
                    showArrow={true}
                    allowClear={true}
                    value={validationTypeInput}
                    onChange={onValidationTypeInputChange}
                    style={{ flex: 2 }}
                    placeholder={t("General.Placeholders.EnterValue", {
                      lngs: i18n.languages,
                      defaultValue: "Enter value",
                    })}
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option!.children as unknown as string).includes(input)
                    }
                    filterSort={(optionA, optionB) =>
                      (optionA!.children as unknown as string)
                        .toLowerCase()
                        .localeCompare(
                          (optionB!.children as unknown as string).toLowerCase()
                        )
                    }
                  >
                    {validationTypes.map((item: any) => {
                      return (
                        <Option value={item ? item : "no-type"}>
                          {item ? item : "No type"}
                        </Option>
                      );
                    })}
                  </AutoComplete>
                </Form.Item>
              </div>
              {RowAction.Upload === drawerAction && (
                <Form.Item
                  label={t(
                    "Page.Configuration.TableSetup.Tabs.References.Labels.References",
                    {
                      lngs: i18n.languages,
                      defaultValue: "References:",
                    }
                  )}
                  tooltip={t(
                    "Page.Configuration.TableSetup.Tabs.References.Tooltips.References",
                    {
                      lngs: i18n.languages,
                      defaultValue: "",
                    }
                  )}
                >
                  <Upload
                    fileList={
                      selectedReferencesFile ? [selectedReferencesFile] : []
                    }
                    onChange={handleReferencesFileChange}
                    beforeUpload={preventRequest}
                    customRequest={customRequest}
                    accept=".xls,.xlsx"
                  >
                    <Button icon={<EditOutlined />} size="small" type="ghost">
                      {" "}
                      {t(
                        "Page.Configuration.TableSetup.Tabs.References.Labels.UploadReferences",
                        {
                          lngs: i18n.languages,
                          defaultValue: "Upload references",
                        }
                      )}
                    </Button>
                  </Upload>
                </Form.Item>
              )}
            </Form>
          )}
        </Drawer>
      </div>
    </div>
  );
};

export default TableSetup;
